import { Dialog } from "primereact/dialog";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { useForeignShopRule } from "../hooks/use-foreignShopRuleData";
import { useProductRule } from "../hooks/use-productRuleData";
import { useOptions } from "../hooks/use-optionsData";
import { useActionRule } from "../hooks/use-actionRuleDate";
import RuleEngineBackend from "../../services/RuleEngineBackend";
import PPUtils from "../../../utils/PPUtils";

function PPPriceManagementForeignShopRuleModal({ visible, close, editingRule }) {
     const [t] = useTranslation();
     const [selectedRule, setSelectedRule] = useState(null);
     const [saveAction, setSaveAction] = useState(null);
     const [canSave, setCanSave] = useState(false);
     const { computeOptions } = useOptions();

     const categories = [
          { label: "Preistracking Mitbewerber", id: 0, value: 0 },
          { label: "Fremdshop Verfügbarkeit", id: 1, value: 1 },
          { label: "Preise von Produktgruppe einbeziehen", id: 2, value: 2 },
     ];

     const keyStringToCategoryId = {
          priceTracking: 0,
          foreignShopstock: 1,
          useAllPricesFromGroup: 2,
     };

     useEffect(() => {
          if (!visible) {
               return;
          }

          computeOptions();

          if (editingRule) {
               const categoryId = keyStringToCategoryId[editingRule.keyString] ?? 1;
               setSelectedRule(categories.find((category) => category.id === categoryId) || categories[0]);
          } else {
               setSelectedRule(categories[0]);
          }
     }, [visible, editingRule]);

     function closeModal() {
          setSelectedRule(null);
          setCanSave(false);
          close();
     }

     const handleSave = () => {
          if (saveAction && canSave) {
               saveAction();
               closeModal();
          }
     };

     return (
          <Dialog
               header={editingRule ? `Regel bearbeiten - ${categories.find((category) => category.id === keyStringToCategoryId[editingRule.keyString])?.label || 'Fremdshop'}` : "Regel - Fremdshop"}
               footer={
                    <>
                         <button className="btn btn-danger btn-lg m-2" onClick={closeModal}>
                              {t("single.cancel")}
                         </button>
                         {selectedRule && selectedRule.id !== undefined && (
                              <button
                                   className="btn btn-primary btn-lg m-2"
                                   onClick={handleSave}
                                   disabled={!canSave}
                              >
                                   <i className="fas fa-save me-2"></i>
                                   {editingRule ? 'Aktualisieren' : 'Speichern'}
                              </button>
                         )}
                    </>
               }
               visible={visible}
               style={{ width: '60vw', height: '70vh' }}
               onHide={closeModal}
          >
               <div className="mb-3">
                    <div className="d-flex flex-wrap gap-2">
                         {categories.map((category) => (
                              <button
                                   key={category.id}
                                   type="button"
                                   className={`btn btn-sm ${selectedRule && selectedRule.id === category.id ? "btn-primary" : "btn-outline-primary"}`}
                                   onClick={() => setSelectedRule(category)}
                              >
                                   {category.label}
                              </button>
                         ))}
                    </div>
               </div>
               <PPPriceManagementForeignShopRuleTile
                    selectedRule={selectedRule}
                    editingRule={editingRule}
                    close={closeModal}
                    setSaveAction={setSaveAction}
                    setCanSave={setCanSave}
               />
          </Dialog>
     );
}
export default PPPriceManagementForeignShopRuleModal;

function PPPriceManagementForeignShopRuleTile({ selectedRule, editingRule, close, setSaveAction, setCanSave }) {
     const { PriceChangeMarginHelper } = useActionRule();

     if (!selectedRule || selectedRule.id === undefined) {
          return null;
     }

     if (selectedRule.id === 0 && PriceChangeMarginHelper.isActive()) {
          return <div className="alert alert-danger m-4" role="alert">
               <p>Preistracking Mitbewerber kann nicht verwendet werden, da bereits eine Preisanpassung nach Marge Komponente existiert!</p>
               <small>Deaktivieren Sie zuerst die Preisanpassung nach Marge, um Preistracking zu verwenden.</small>
          </div>;
     }

     switch (selectedRule.id) {
          case 0:
               return <PPPriceManagementPricetrackingModal
                    closeModal={close}
                    editingRule={editingRule}
                    setSaveAction={setSaveAction}
                    setCanSave={setCanSave}
               />;
          case 1:
               return <PPPriceManagementForeignStockModal
                    closeModal={close}
                    editingRule={editingRule}
                    setSaveAction={setSaveAction}
                    setCanSave={setCanSave}
               />;
          case 2:
               return <PPPriceManagementUseAllPricesFromGroupModal
                    closeModal={close}
                    editingRule={editingRule}
                    setSaveAction={setSaveAction}
                    setCanSave={setCanSave}
               />;
          default:
               return "";
     }
}

function PPPriceManagementPricetrackingModal({ setSaveAction, setCanSave, editingRule }) {

     const { getForeignShopRuleOptions, fetchPriceTrackingShops } = useOptions();
     const options = getForeignShopRuleOptions().priceTracking;

     const { PriceTrackingHelper } = useForeignShopRule();
     const { LanguageAndChannelHelper } = useProductRule();
     const auth = PPUtils.getLogin();
     const token = auth.token;
     const customerId = PPUtils.getEffectiveCustomerId(auth);

     const isEditing = editingRule && editingRule.keyString === 'priceTracking';
     const [providerFilters, setProviderFilters] = useState([]);
     const [selectedProviderFilter, setSelectedProviderFilter] = useState(null);
     const [useAllProviders, setUseAllProviders] = useState(() => PriceTrackingHelper.getUseAllProviders());
     const [excludeDeliveryCosts, setExcludeDeliveryCosts] = useState(
          () => PriceTrackingHelper.getExcludeShopIfDeliveryCostAreNotAvailable()
     );

     const priorityMin = options.priorityRange?.min || 1;
     const priorityMax = options.priorityRange?.max || 10;
     const priorityOptions = Array.from({ length: priorityMax - priorityMin + 1 }, (_, i) => ({
          value: priorityMin + i,
          label: (priorityMin + i).toString()
     }));

     const [shopList, setShopList] = useState([]);
     const [activeShopList, setActiveShopList] = useState([]);
     const [contextShops, setContextShops] = useState([]);
     const activeLanguage = LanguageAndChannelHelper.getLanguage()?.value ||
          LanguageAndChannelHelper.getLanguage()?.label ||
          LanguageAndChannelHelper.getLanguage() ||
          null;

     useEffect(() => {
          let cancelled = false;

          async function loadContextShops() {
               const shops = await fetchPriceTrackingShops(activeLanguage);
               if (!cancelled) {
                    setContextShops(shops);
               }
          }

          loadContextShops();
          return () => {
               cancelled = true;
          };
     }, [activeLanguage, fetchPriceTrackingShops]);

     useEffect(() => {
          const shopKeysWithPrio = isEditing
               ? editingRule?.shopKeysWithPrio || {}
               : PriceTrackingHelper.getShopKeysWithPrio() || {};
          const activeShopIds = new Set(Object.keys(shopKeysWithPrio).map(Number));
          const knownShopsById = new Map(
               [...contextShops, ...options.shops].map((shop) => [Number(shop.id), shop])
          );
          const selectedShops = [...activeShopIds].map((shopId) => {
               const shop = knownShopsById.get(shopId);
               return shop ? { ...shop, priority: Number(shopKeysWithPrio[shopId]) } : {
                    id: shopId,
                    name: `Shop ${shopId}`,
                    lang: "",
                    priority: Number(shopKeysWithPrio[shopId])
               };
          });
          setActiveShopList(
               selectedShops
          );
          setShopList(contextShops.filter((shop) => !activeShopIds.has(Number(shop.id))));
     }, [contextShops, options.shops, isEditing, editingRule, PriceTrackingHelper]);

     const sortedActiveShopList = [...activeShopList].sort((left, right) => {
          const leftPrio = Number.isFinite(left.priority) ? left.priority : Number.MAX_SAFE_INTEGER;
          const rightPrio = Number.isFinite(right.priority) ? right.priority : Number.MAX_SAFE_INTEGER;
          if (leftPrio !== rightPrio) {
               return leftPrio - rightPrio;
          }
          return left.name.localeCompare(right.name);
     });

     useEffect(() => {
          async function loadProviderFilters() {
               const filters = await RuleEngineBackend.getProviderFilters(token, customerId);
               if (Array.isArray(filters)) {
                    setProviderFilters(filters);
               }
          }
          loadProviderFilters();
     }, [token, customerId]);

     useEffect(() => {
          const pf = PriceTrackingHelper.getProviderFilter();
          if (pf?.filter_id && pf.filter_id !== -1) {
               const match = providerFilters.find((f) => f.id === pf.filter_id);
               if (match) {
                    setSelectedProviderFilter({ value: match.id, label: match.name });
               }
          } else {
               setSelectedProviderFilter(null);
          }
     }, [isEditing, editingRule?.useAllProviders, editingRule?.excludeShopIfDeliveryCostAreNotAvailable, providerFilters]);

     useEffect(() => {
          setSaveAction(() => () => {
               PriceTrackingHelper.setActive(true);
               PriceTrackingHelper.setUseAllProviders(useAllProviders);
               PriceTrackingHelper.setExcludeShopIfDeliveryCostAreNotAvailable(excludeDeliveryCosts);

               const shopKeysWithPrio = {};
               activeShopList.forEach(shop => {
                    shopKeysWithPrio[shop.id] = shop.priority;
               });
               PriceTrackingHelper.setShopKeysWithPrio(shopKeysWithPrio);
               if (selectedProviderFilter?.value) {
                    PriceTrackingHelper.setProviderFilter({
                         filter_id: selectedProviderFilter.value,
                         filter_values: ""
                    });
               } else {
                    PriceTrackingHelper.setProviderFilter({ filter_id: -1, filter_values: "" });
               }
          });

          const hasConfig = Array.isArray(activeShopList) && activeShopList.length > 0;
          setCanSave(hasConfig);
     }, [
          setSaveAction,
          activeShopList,
          selectedProviderFilter,
          useAllProviders,
          excludeDeliveryCosts,
          PriceTrackingHelper,
          setCanSave
     ]);

     const [inactiveSelectedShop, setInactiveSelectedShop] = useState();
     const [activeSelectedShop, setActiveSelectedShop] = useState();

     function handleToActiveClicked() {
          setShopList(prev => [...prev].filter(el => el.id !== inactiveSelectedShop.id));
          setActiveShopList(prev => [...prev, { ...inactiveSelectedShop, priority: priorityMin }]);
          setInactiveSelectedShop(null);
     }

     function handleToInactiveClicked() {
          setActiveShopList(prev => [...prev].filter(el => el.id !== activeSelectedShop.id));
          const contextShop = contextShops.find((shop) => Number(shop.id) === Number(activeSelectedShop.id));
          if (contextShop) {
               setShopList(prev => [...prev, { ...contextShop, priority: undefined }]);
          }
          setActiveSelectedShop(null);
     }

     function handlePriorityChange(shopId, newPriority) {
          setActiveShopList(prev => prev.map(shop =>
               shop.id === shopId ? { ...shop, priority: newPriority } : shop
          ));
     }

     return (
          <div className="pt-3 pp-form-stack">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">Shop-Auswahl</h5>
                    </div>
                    <div className="card-body">
                         <div className="row">
                              <div className="col-5">
                                   <h6 className="text-muted mb-3">Verfügbare Shops</h6>
                                   <div className="border rounded p-3" style={{ minHeight: '200px', maxHeight: '300px', overflowY: 'auto' }}>
                                        {shopList.map((shop) => (
                                             <button
                                                  type="button"
                                                  key={shop.id}
                                                  className={`p-2 mb-2 rounded cursor-pointer ${inactiveSelectedShop?.id === shop.id ? 'bg-primary text-white' : 'bg-light'}`}
                                                  onClick={() => inactiveSelectedShop?.id === shop.id ? setInactiveSelectedShop(null) : setInactiveSelectedShop(shop)}
                                                  style={{ cursor: 'pointer', width: '100%', textAlign: 'left', border: 'none' }}
                                             >
                                                  {shop.name} ({shop.lang})
                                             </button>
                                        ))}
                                   </div>
                              </div>

                              <div className="col-2 d-flex flex-column justify-content-center align-items-center">
                                   <button
                                        className="btn btn-outline-primary mb-2"
                                        disabled={!inactiveSelectedShop}
                                        onClick={handleToActiveClicked}
                                        title="Shop hinzufügen"
                                   >
                                        <i className="fas fa-arrow-right"></i>
                                   </button>
                                   <button
                                        className="btn btn-outline-secondary"
                                        disabled={!activeSelectedShop}
                                        onClick={handleToInactiveClicked}
                                        title="Shop entfernen"
                                   >
                                        <i className="fas fa-arrow-left"></i>
                                   </button>
                              </div>

                              <div className="col-5">
                                   <h6 className="text-muted mb-3">Ausgewählte Shops</h6>
                                   <div className="border rounded p-3" style={{ minHeight: '200px', maxHeight: '300px', overflowY: 'auto' }}>
                                       {sortedActiveShopList.map((shop) => (
                                             <div
                                                  key={shop.id}
                                                  className={`p-2 mb-2 rounded d-flex justify-content-between align-items-center ${activeSelectedShop?.id === shop.id ? 'bg-primary text-white' : 'bg-light'}`}
                                             >
                                                  <button
                                                       type="button"
                                                       className="flex-grow-1 text-start"
                                                       style={{ background: 'none', border: 'none', padding: 0, color: 'inherit' }}
                                                       onClick={() => activeSelectedShop?.id === shop.id ? setActiveSelectedShop(null) : setActiveSelectedShop(shop)}
                                                  >
                                                       {shop.name} ({shop.lang})
                                                  </button>
                                                  <div className="d-flex align-items-center">
                                                       <small className="me-1">Prio:</small>
                                                       <select
                                                            className="form-select form-select-sm"
                                                            style={{ width: '60px', fontSize: '0.8rem' }}
                                                            value={shop.priority || priorityMin}
                                                            onChange={(e) => {
                                                                 e.stopPropagation();
                                                                 handlePriorityChange(shop.id, parseInt(e.target.value));
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                       >
                                                            {priorityOptions.map(option => (
                                                                 <option key={option.value} value={option.value}>
                                                                      {option.label}
                                                                 </option>
                                                            ))}
                                                       </select>
                                                  </div>
                                             </div>
                                        ))}
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>

               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">Einstellungen</h5>
                    </div>
                    <div className="card-body">
                         <div className="ps-3">
                              <div className="form-check form-switch mb-3">
                                   <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="useAllProviders"
                                        checked={!useAllProviders}
                                        onChange={() => setUseAllProviders(false)}
                                   />
                                   <label className="form-check-label" htmlFor="useAllProviders">
                                        Nur den günstigsten Anbieter eines Preisportals berücksichtigen
                                   </label>
                              </div>

                              <div className="form-check form-switch mb-3">
                                   <input
                                        className="form-check-input"
                                        type="checkbox"
                                        role="switch"
                                        id="useAllProvidersOption"
                                        checked={useAllProviders}
                                        onChange={() => setUseAllProviders(true)}
                                   />
                                   <label className="form-check-label" htmlFor="useAllProvidersOption">
                                        Alle Anbieter der Preisportale berücksichtigen
                                   </label>
                              </div>

                              <div className="border-top pt-3 mb-3">
                                   <h6 className="text-muted mb-3">Anbieterfilter</h6>
                                   <Select
                                        className="w-100"
                                        options={providerFilters.map((filter) => ({
                                             value: filter.id,
                                             label: filter.name
                                        }))}
                                        value={selectedProviderFilter}
                                        onChange={(selected) => {
                                             setSelectedProviderFilter(selected);
                                        }}
                                        placeholder="Anbieterfilter aus globalen Einstellungen wählen..."
                                        isClearable
                                   />
                              </div>

                              <div className="border-top pt-3">
                                   <h6 className="text-muted mb-3">Ausschlusskriterium</h6>
                                   <div className="form-check form-switch">
                                        <input
                                             className="form-check-input"
                                             type="checkbox"
                                             role="switch"
                                             id="excludeDeliveryCosts"
                                        checked={excludeDeliveryCosts}
                                        onChange={() => setExcludeDeliveryCosts((current) => !current)}
                                        />
                                        <label className="form-check-label" htmlFor="excludeDeliveryCosts">
                                             Versandkosten nicht verfügbar
                                        </label>
                                   </div>
                              </div>
                         </div>
                    </div>
               </div>
          </div>
     );
}

function PPPriceManagementForeignStockModal({ closeModal, editingRule, setSaveAction, setCanSave }) {

     const { ForeignShopStockHelper } = useForeignShopRule();
     const { StockHelper } = useProductRule();
     const { getForeignShopRuleOptions } = useOptions();

     const { stock } = getForeignShopRuleOptions().foreignShopstock;
     const availableStockRules = StockHelper.getStockRules();

     const isEditing = editingRule && editingRule.keyString === 'foreignShopstock';

     const [stockList, setStockList] = useState(() => {
          if (isEditing) {
               return editingRule.stock || [false, false, false];
          }
          return [false, false, false];
     });

     const [selectedStockReference, setSelectedStockReference] = useState(() => {
          if (isEditing && editingRule.stockRuleReference) {
               const referencedRule = availableStockRules.find(rule => rule.id === editingRule.stockRuleReference);
               if (referencedRule) {
                    return {
                         label: `Verfügbarkeit: ${referencedRule.stock.map((active, index) =>
                              active ? stock[index] : null
                         ).filter(Boolean).join(", ") || "Alle"}`,
                         id: referencedRule.id,
                         stockRule: referencedRule
                    };
               }
          }
          return null;
     });

     useEffect(() => {
          setSaveAction(() => () => {
               if (!selectedStockReference) {
                    return;
               }

               if (selectedStockReference) {
                    if (isEditing) {
                         ForeignShopStockHelper.updateForeignShopStockRule(editingRule.id, {
                              stock: stockList,
                              stockRuleReference: selectedStockReference.id
                         });
                    } else {
                         ForeignShopStockHelper.addForeignShopStockRule({
                              stock: stockList,
                              stockRuleReference: selectedStockReference.id
                         });
                    }
               }
          });

          const hasStockSelection =
               Array.isArray(stockList) && stockList.some(Boolean);
          const hasReference = !!selectedStockReference;
          setCanSave(hasReference && hasStockSelection);
     }, [setSaveAction, isEditing, editingRule?.id, stockList, selectedStockReference, ForeignShopStockHelper, setCanSave]);

     function changeStockList(index, state) {
          setStockList((prev) => {
               const newList = [...prev];
               newList[index] = state;
               return newList;
          });
     }

     if (availableStockRules.length === 0) {
          return <div className="alert alert-danger m-4" role="alert">
               <p>Diese Komponente kann nicht verwendet werden, solange keine Verfügbarkeits-Regel im Bereich &quot;Eigene Produkte&quot; erstellt wurde!</p>
               <small>Erstellen Sie zuerst eine Verfügbarkeits-Regel unter &quot;Eigene Produkte&quot; → &quot;Verfügbarkeit&quot;.</small>
          </div>;
     }

     const stockReferenceOptions = availableStockRules.map(stockRule => ({
          label: `Verfügbarkeit: ${stockRule.stock.map((active, index) =>
               active ? stock[index] : null
          ).filter(Boolean).join(", ") || "Alle"}`,
          id: stockRule.id,
          stockRule: stockRule
     }));

     return (
          <div className="pt-3">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">
                              {isEditing ? 'Fremdshop Verfügbarkeit bearbeiten' : 'Fremdshop Verfügbarkeit'}
                         </h5>
                         {isEditing && (
                              <small className="text-muted">
                                   Regel-ID: {editingRule.id}
                              </small>
                         )}
                    </div>
                    <div className="card-body">
                         <div className="mb-3">
                              <label htmlFor="foreignShop_rule_select" className="form-label">Referenz auf Verfügbarkeits-Regel:</label>
                              <Select
                                   className="w-100"
                                   inputId="foreignShop_rule_select"
                                   options={stockReferenceOptions}
                                   value={selectedStockReference}
                                   onChange={(element) => setSelectedStockReference(element)}
                                   placeholder="Wählen Sie eine Verfügbarkeits-Regel..."
                              />
                              <small className="text-muted">
                                   Diese Fremdshop-Regel bezieht sich auf die ausgewählte Verfügbarkeits-Regel.
                              </small>
                         </div>

                         {selectedStockReference && (
                              <div className="ps-3">
                                   <label htmlFor="foreign_stock_red" className="form-label">Fremdshop Verfügbarkeitsstatus:</label>
                                   <div className="form-check mb-3">
                                        <input
                                             type="checkbox"
                                             className="form-check-input"
                                             id="foreign_stock_red"
                                             onChange={(event) => changeStockList(0, event.target.checked)}
                                             checked={stockList[0]}
                                        />
                                        <label className="form-check-label" htmlFor="foreign_stock_red">
                                             {stock[0]}
                                        </label>
                                   </div>
                                   <div className="form-check mb-3">
                                        <input
                                             type="checkbox"
                                             className="form-check-input"
                                             id="foreign_stock_green"
                                             onChange={(event) => changeStockList(1, event.target.checked)}
                                             checked={stockList[1]}
                                        />
                                        <label className="form-check-label" htmlFor="foreign_stock_green">
                                             {stock[1]}
                                        </label>
                                   </div>
                                   <div className="form-check mb-0">
                                        <input
                                             type="checkbox"
                                             className="form-check-input"
                                             id="foreign_stock_yellow"
                                             onChange={(event) => changeStockList(2, event.target.checked)}
                                             checked={stockList[2]}
                                        />
                                        <label className="form-check-label" htmlFor="foreign_stock_yellow">
                                             {stock[2]}
                                        </label>
                                   </div>
                              </div>
                         )}
                    </div>
               </div>

               {isEditing && (
                    <div className="d-flex justify-content-end mt-3">
                         <button
                              className="btn btn-danger"
                              onClick={() => {
                                   ForeignShopStockHelper.removeForeignShopStockRule(editingRule.id);
                                   closeModal();
                              }}
                         >
                              <i className="fas fa-trash me-2"></i>{' '}Löschen
                         </button>
                    </div>
               )}
          </div>
     );
}

function PPPriceManagementUseAllPricesFromGroupModal({ closeModal, editingRule, setSaveAction, setCanSave }) {

     const { UseAllPricesFromGroupHelper, PriceTrackingHelper } = useForeignShopRule();
     const auth = PPUtils.getLogin();
     const token = auth.token;

     const isEditing = editingRule && editingRule.keyString === "useAllPricesFromGroup";

     const [groupKeys, setGroupKeys] = useState([]);
     const [selectedGroup, setSelectedGroup] = useState(() => {
          const groupName = isEditing
               ? editingRule.groupName
               : UseAllPricesFromGroupHelper.getGroupName();
          return groupName ? { label: groupName, value: groupName } : null;
     });

     useEffect(() => {
          async function loadGroupKeys() {
               const keys = await RuleEngineBackend.getProductAttributeTextKeys(token);
               if (Array.isArray(keys)) {
                    setGroupKeys(keys.map((key) => ({ label: key, value: key })));
               }
          }
          loadGroupKeys();
     }, [token]);

     useEffect(() => {
          setSaveAction(() => () => {
               if (!selectedGroup?.value) {
                    return;
               }
               UseAllPricesFromGroupHelper.setGroupName(selectedGroup.value);
               UseAllPricesFromGroupHelper.setActive(true);
          });

          setCanSave(!!selectedGroup?.value);
     }, [setSaveAction, setCanSave, selectedGroup, UseAllPricesFromGroupHelper]);

     if (!PriceTrackingHelper.isActive()) {
          return <div className="alert alert-danger m-4" role="alert">
               <p>Diese Komponente kann nicht verwendet werden, solange keine &quot;Preistracking Mitbewerber&quot;-Komponente erstellt wurde!</p>
               <small>Erstellen Sie zuerst eine Preistracking-Komponente, um diese Funktion zu verwenden.</small>
          </div>;
     }

     return (
          <div className="pt-3">
               <div className="card">
                    <div className="card-header">
                         <h5 className="mb-0">
                              {isEditing ? 'Preise von Produktgruppe einbeziehen bearbeiten' : 'Preise von Produktgruppe einbeziehen'}
                         </h5>
                    </div>
                    <div className="card-body">
                         <div className="mb-3">
                              <label htmlFor="use_all_prices_from_group_select" className="form-label">
                                   Wählen Sie eine Produktgruppe, von der jeweils der günstigste verknüpfte
                                   Fremdshop-Preis für die Preisberechnung verwendet werden soll:
                              </label>
                              <Select
                                   className="w-100"
                                   inputId="use_all_prices_from_group_select"
                                   options={groupKeys}
                                   value={selectedGroup}
                                   onChange={(element) => setSelectedGroup(element)}
                                   placeholder="Produktgruppe wählen..."
                              />
                         </div>
                    </div>
               </div>

               {isEditing && (
                    <div className="d-flex justify-content-end mt-3">
                         <button
                              className="btn btn-danger"
                              onClick={() => {
                                   UseAllPricesFromGroupHelper.setGroupName("");
                                   UseAllPricesFromGroupHelper.setActive(false);
                                   closeModal();
                              }}
                         >
                              <i className="fas fa-trash me-2"></i>{' '}Löschen
                         </button>
                    </div>
               )}
          </div>
     );
}